import { createContext, ReactNode, useContext, useState } from "react"
import { MainContext } from "./main"
import { AuthContext } from "./auth"
import {
  deepCopy,
  logger,
  roundWithDecimalPlaces,
  Status,
} from "../services/utils/utils"
import DashboardCardData from "../models/dashboardCardData"
import { useTranslation } from "react-i18next"
import ChallengeLeaderboardItem from "../models/challengeLeaderboardItem"
import { useLazyQuery } from "@apollo/client"
import {
  dashboardActivitiesByCategoryList,
  dashboardActivityOverviewList,
  dashboardAverageUserGet,
  dashboardChallengeGroupLeaderboardGet,
  dashboardChallengeLeaderboardGet,
  dashboardChallengeList,
  dashboardDailyChallengeOverviewGet,
  dashboardSavingsGet,
  dashboardTotalEducationHoursGet,
  dashboardTotalEducationHoursUsersGet,
  dashboardTotalNewsGet,
  dashboardTotalSurveyGet,
  dashboardTotalUsersGet,
} from "../services/graphql/queries"
import Challenge from "../models/challenge"
import User from "../models/user"
import ChallengeOverviewDataItem from "../models/challengeOverviewDataItem"
import OverviewCardDataItem from "../models/overviewCardDataItem"
import ByCategoryCardDataItem from "../models/byCategoryCardDataItem"
import TeamSavingsCardDataItem from "../models/teamSavingsCardDataItem"
import ChallengeGroupsLeaderboardItem from "../models/challengeGroupsLeaderboardItem"

interface OverviewContextInterface {
  overviewDataLoading: boolean
  dashboardCardData: DashboardCardData | null
  challenge: Challenge | null
  leaderboard: ChallengeLeaderboardItem[]
  leaderboardNextToken: string | null
  groupsLeaderboard: ChallengeGroupsLeaderboardItem[]
  groupsLeaderboardNextToken: string | null
  educationHoursUsersList: {
    totalEducationHours: number
    user: User
  }[]
  educationHoursUsersListNextToken: string | null
  challengeOverviewData: ChallengeOverviewDataItem[]
  overviewCardData: OverviewCardDataItem[]
  byCategoriesCardData: ByCategoryCardDataItem[]
  teamSavingsCardData: TeamSavingsCardDataItem | null
  loadMoreLeaderboardItems: () => Promise<boolean>
  loadMoreGroupsLeaderboardItems: () => Promise<boolean>
  loadMoreEducationHoursUsersListItems: () => Promise<boolean>
  totalNews: number | null
  totalSurveys: number | null
  getOverviewData: () => Promise<void>
}

const OverviewContext = createContext<OverviewContextInterface>({
  overviewDataLoading: true,
  dashboardCardData: null,
  challenge: null,
  leaderboard: [],
  leaderboardNextToken: null,
  groupsLeaderboard: [],
  groupsLeaderboardNextToken: null,
  educationHoursUsersList: [],
  educationHoursUsersListNextToken: null,
  challengeOverviewData: [],
  overviewCardData: [],
  byCategoriesCardData: [],
  teamSavingsCardData: null,
  loadMoreLeaderboardItems: async () => true,
  loadMoreGroupsLeaderboardItems: async () => true,
  loadMoreEducationHoursUsersListItems: async () => true,
  totalNews: null,
  totalSurveys: null,
  getOverviewData: async () => {},
})

const OverviewController = ({ children }: { children: ReactNode }) => {
  const { t, i18n } = useTranslation()
  const { team } = useContext(AuthContext)
  const { getInput, setError, setErrorMessage } = useContext(MainContext)

  // states
  const [overviewDataLoading, setOverviewDataLoading] = useState<boolean>(false)
  const [dashboardCardData, setDashboardCardData] =
    useState<DashboardCardData | null>(null)
  const [challenge, setChallenge] = useState<Challenge | null>(null)
  const [leaderboard, setLeaderboard] = useState<ChallengeLeaderboardItem[]>([])
  const [leaderboardNextToken, setLeaderboardNextToken] = useState<
    string | null
  >(null)
  const [groupsLeaderboard, setGroupsLeaderboard] = useState<
    ChallengeGroupsLeaderboardItem[]
  >([])
  const [groupsLeaderboardNextToken, setGroupsLeaderboardNextToken] = useState<
    string | null
  >(null)
  const [educationHoursUsersList, setEducationHoursUsersList] = useState<
    {
      totalEducationHours: number
      user: User
    }[]
  >([])
  const [
    educationHoursUsersListNextToken,
    setEducationHoursUsersListNextToken,
  ] = useState<string | null>(null)
  const [challengeOverviewData, setChallengeOverviewData] = useState<
    ChallengeOverviewDataItem[]
  >([])
  const [overviewCardData, setOverviewCardData] = useState<
    OverviewCardDataItem[]
  >([])
  const [byCategoriesCardData, setByCategoriesCardData] = useState<
    ByCategoryCardDataItem[]
  >([])
  const [teamSavingsCardData, setTeamSavingsCardData] =
    useState<TeamSavingsCardDataItem | null>(null)
  const [totalNews, setTotalNews] = useState<number | null>(null)
  const [totalSurveys, setTotalSurveys] = useState<number | null>(null)

  // queries
  const [dashboardTotalUsersGetQuery] = useLazyQuery(dashboardTotalUsersGet)
  const [dashboardAverageUserGetQuery] = useLazyQuery(dashboardAverageUserGet)
  const [dashboardChallengeListQuery] = useLazyQuery(dashboardChallengeList)
  const [dashboardChallengeLeaderboardGetQuery] = useLazyQuery(
    dashboardChallengeLeaderboardGet
  )
  const [dashboardChallengeGroupLeaderboardGetQuery] = useLazyQuery(
    dashboardChallengeGroupLeaderboardGet
  )
  const [dashboardTotalEducationHoursUsersGetQuery] = useLazyQuery(
    dashboardTotalEducationHoursUsersGet
  )
  const [dashboardDailyChallengeOverviewGetQuery] = useLazyQuery(
    dashboardDailyChallengeOverviewGet
  )
  const [dashboardActivityOverviewListQuery] = useLazyQuery(
    dashboardActivityOverviewList
  )
  const [dashboardActivitiesByCategoryListQuery] = useLazyQuery(
    dashboardActivitiesByCategoryList
  )
  const [dashboardSavingsGetQuery] = useLazyQuery(dashboardSavingsGet)
  const [dashboardTotalEducationHoursGetQuery] = useLazyQuery(
    dashboardTotalEducationHoursGet
  )
  const [dashboardTotalNewsGetQuery] = useLazyQuery(dashboardTotalNewsGet)
  const [dashboardTotalSurveyGetQuery] = useLazyQuery(dashboardTotalSurveyGet)

  // get dashboard card
  const getDashboardCardData = async () => {
    try {
      logger(Status.Api, "[ENG] QUERY totalUsersGet")
      logger(Status.Api, "[ENG] QUERY totalEducationHoursGet")
      logger(Status.Api, "[ENG] QUERY averageUserGet")
      logger(Status.Api, "[ENG] QUERY mostActiveUserGet")

      const input = getInput(team!.id)

      const result = await Promise.all([
        dashboardTotalUsersGetQuery({
          variables: { input: input },
        }),
        dashboardTotalEducationHoursGetQuery({
          variables: { input: input },
        }),
        dashboardAverageUserGetQuery({
          variables: { input: input },
        }),
      ])
      const dataToSet: DashboardCardData = {
        totalUsers: result[0].data.dashboardTotalUsersGet
          ? result[0].data.dashboardTotalUsersGet.users
          : null,
        activeUsersPercentage: result[0].data.dashboardTotalUsersGet
          ? roundWithDecimalPlaces(
              result[0].data.dashboardTotalUsersGet.activeUsersPercentage * 100,
              1
            )
          : null,
        totalEducationHours: result[1].data.dashboardTotalEducationHoursGet
          ? Math.round(
              result[1].data.dashboardTotalEducationHoursGet.totalEducationHours
            )
          : null,
        avgEducationHoursPerUser: result[1].data.dashboardTotalEducationHoursGet
          ? roundWithDecimalPlaces(
              result[1].data.dashboardTotalEducationHoursGet
                .avgEducationHoursPerUser,
              2
            )
          : null,
        averageUser: {
          country: result[2].data.dashboardAverageUserGet
            ? result[2].data.dashboardAverageUserGet.country
            : null,
          percentage: result[2].data.dashboardAverageUserGet
            ? roundWithDecimalPlaces(
                result[2].data.dashboardAverageUserGet.percentage * 100,
                1
              )
            : null,
        },
      }
      logger(Status.Info, "[ENG] dashboard card data", dataToSet)

      setDashboardCardData(dataToSet)

      return true
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} Dashboard Card`)
      setDashboardCardData(null)

      return false
    }
  }

  // get challenge card
  const getChallengeCardData = async () => {
    try {
      logger(Status.Api, "[ENG] QUERY challengeList")
      logger(Status.Api, "[ENG] QUERY challengeLeaderboardGet")
      logger(Status.Api, "[ENG] QUERY challengeGroupLeaderboardGet")

      const input = getInput(team!.id)

      const result = await Promise.all([
        dashboardChallengeListQuery({
          variables: {
            input: {
              teamId: team!.id,
              lang: i18n.language,
              limit: 50,
            },
          },
        }),
        dashboardChallengeLeaderboardGetQuery({
          variables: { input: { ...input, limit: 50 } },
        }),
        dashboardChallengeGroupLeaderboardGetQuery({
          variables: { input: { ...input, limit: 50 } },
        }),
      ])

      // set active challenge as current from challenges list
      const items = result[0].data.dashboardChallengeList.items
      const now = new Date()
      const activeItem = items.find((item: any) => {
        const startsAt = new Date(item.challenge.startsAt)
        const endsAt = new Date(item.challenge.endsAt)
        return now >= startsAt && now <= endsAt
      })

      const challengeDataToSet = activeItem
        ? {
            ...activeItem.challenge,
            users: activeItem.users,
            activeUsers: activeItem.activeUsers,
          }
        : null

      // parse leaderboard data
      let leaderboardDataToSet = deepCopy(
        result[1].data.dashboardChallengeLeaderboardGet.items
      )
      leaderboardDataToSet = leaderboardDataToSet.filter(
        (item: any) =>
          item.user.first_name !== "deleted" &&
          item.user.last_name !== "deleted"
      )
      leaderboardDataToSet.forEach((item: any) => {
        if (item.userPointsDeltaPercentage) {
          item.userPointsDeltaPercentage = roundWithDecimalPlaces(
            item.userPointsDeltaPercentage * 100,
            1
          )
        }
      })

      logger(Status.Info, "[ENG] challenge", challengeDataToSet)
      logger(Status.Info, "[ENG] challenge leaderboard", leaderboardDataToSet)
      logger(
        Status.Info,
        "[ENG] challenge groups leaderboard",
        result[2].data.dashboardChallengeGroupLeaderboardGet.items
      )

      setChallenge(challengeDataToSet)
      setLeaderboard(leaderboardDataToSet)
      setLeaderboardNextToken(
        result[1].data.dashboardChallengeLeaderboardGet.nextToken
      )
      setGroupsLeaderboard(
        result[2].data.dashboardChallengeGroupLeaderboardGet.items
      )
      setGroupsLeaderboardNextToken(
        result[2].data.dashboardChallengeGroupLeaderboardGet.nextToken
      )

      return true
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} Challenge`)
      setChallenge(null)
      setLeaderboard([])
      setLeaderboardNextToken(null)
      setGroupsLeaderboard([])
      setGroupsLeaderboardNextToken(null)

      return false
    }
  }

  // get education hours users list
  const getEducationHoursUsersList = async () => {
    try {
      logger(Status.Api, "[ENG] QUERY totalEducationHoursUsersGet")

      const input = getInput(team!.id)

      const { data } = await dashboardTotalEducationHoursUsersGetQuery({
        variables: { input: { ...input, limit: 50 } },
      })

      logger(
        Status.Info,
        "[ENG] education hours users list",
        data.dashboardTotalEducationHoursUsersGet.items
      )

      setEducationHoursUsersList(
        data.dashboardTotalEducationHoursUsersGet.items
      )
      setEducationHoursUsersListNextToken(
        data.dashboardTotalEducationHoursUsersGet.nextToken
      )

      return true
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} Education Hours Users List`)
      setEducationHoursUsersList([])
      setEducationHoursUsersListNextToken(null)

      return false
    }
  }

  // get challenge overview card
  const getDailyChallengeOverview = async () => {
    try {
      logger(Status.Api, "[ENG] QUERY dailyChallengeOverviewGet")

      const input = getInput(team!.id)
      let nextToken = null
      let dataToSet = []

      do {
        const result: any = await dashboardDailyChallengeOverviewGetQuery({
          variables: {
            input: { ...input, limit: 10000, nextToken: nextToken },
          },
        })

        dataToSet.push(...result.data.dashboardDailyChallengeOverviewGet.items)
        nextToken = result.data.dashboardDailyChallengeOverviewGet.nextToken
      } while (nextToken)

      dataToSet = dataToSet.sort(
        (a, b) =>
          new Date(a.activityDate).getTime() -
          new Date(b.activityDate).getTime()
      )

      logger(Status.Info, "[ENG] challenge overview data", dataToSet)

      setChallengeOverviewData(dataToSet)

      return true
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} Challenge Overview`)
      setChallengeOverviewData([])

      return false
    }
  }

  // get overview overtime card
  const getOverviewCardData = async () => {
    try {
      logger(Status.Api, "[ENG] QUERY activityOverviewList")

      const input = getInput(team!.id)
      let nextToken = null
      const dataToSet = []

      do {
        const result: any = await dashboardActivityOverviewListQuery({
          variables: {
            input: { ...input, limit: 10000, nextToken: nextToken },
          },
        })

        dataToSet.push(...result.data.dashboardActivityOverviewList.items)
        nextToken = result.data.dashboardActivityOverviewList.nextToken
      } while (nextToken)

      logger(Status.Info, "[ENG] overview overtime card data", dataToSet)

      setOverviewCardData(dataToSet)

      return true
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} Overview Card`)
      setOverviewCardData([])

      return false
    }
  }

  // get by category card
  const getByCategoryCardData = async () => {
    try {
      logger(Status.Api, "[ENG] QUERY activitiesByCategoryList")

      const input = getInput(team!.id)

      const { data } = await dashboardActivitiesByCategoryListQuery({
        variables: { input: { ...input, limit: 100 } },
      })
      logger(
        Status.Info,
        "[ENG] by cateogory card data",
        data.dashboardActivitiesByCategoryList.items
      )

      setByCategoriesCardData(data.dashboardActivitiesByCategoryList.items)

      return true
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} By Category Card`)
      setByCategoriesCardData([])

      return false
    }
  }

  // get team savings card
  const getTeamSavingsCardData = async () => {
    try {
      logger(Status.Api, "[ENG] QUERY savingsGet")

      const input = getInput(team!.id)

      const { data } = await dashboardSavingsGetQuery({
        variables: { input: input },
      })

      // parse data
      const dataToSet = data.dashboardSavingsGet
        ? {
            bathTubSaving: roundWithDecimalPlaces(
              data.dashboardSavingsGet.bathTubSaving,
              1
            ),
            co2Saving: roundWithDecimalPlaces(
              data.dashboardSavingsGet.co2Saving,
              1
            ),
            dishWasherSaving: roundWithDecimalPlaces(
              data.dashboardSavingsGet.dishWasherSaving,
              1
            ),
            energySaving: roundWithDecimalPlaces(
              data.dashboardSavingsGet.energySaving,
              1
            ),
            phoneChargeSaving: roundWithDecimalPlaces(
              data.dashboardSavingsGet.phoneChargeSaving,
              1
            ),
            waterSaving: roundWithDecimalPlaces(
              data.dashboardSavingsGet.waterSaving,
              1
            ),
          }
        : null
      logger(Status.Info, "[ENG] team savings card data", dataToSet)

      setTeamSavingsCardData(dataToSet)

      return true
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} Savings`)
      setTeamSavingsCardData(null)

      return false
    }
  }

  // get total news
  const getTotalNews = async () => {
    try {
      logger(Status.Api, "[ENG] QUERY totalNewsGet")

      const input: any = getInput(team!.id)
      delete input.lang

      const { data } = await dashboardTotalNewsGetQuery({
        variables: { input },
      })

      logger(
        Status.Info,
        "[ENG] total news",
        data.dashboardTotalNewsGet ? data.dashboardTotalNewsGet.totalNews : 0
      )

      setTotalNews(
        data.dashboardTotalNewsGet ? data.dashboardTotalNewsGet.totalNews : 0
      )

      return true
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} Total News`)
      setTotalNews(null)

      return false
    }
  }

  // get total surveys
  const getTotalSurveys = async () => {
    try {
      logger(Status.Api, "[ENG] QUERY totalSurveyGet")

      const input: any = getInput(team!.id)
      delete input.lang

      const { data } = await dashboardTotalSurveyGetQuery({
        variables: { input },
      })

      logger(
        Status.Info,
        "[ENG] total surveys",
        data.dashboardTotalSurveyGet
          ? data.dashboardTotalSurveyGet.totalSurvey
          : 0
      )

      setTotalSurveys(
        data.dashboardTotalSurveyGet
          ? data.dashboardTotalSurveyGet.totalSurvey
          : 0
      )

      return true
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} Total Surveys`)
      setTotalSurveys(null)

      return false
    }
  }

  // load more leaderboard
  const loadMoreLeaderboardItems = async () => {
    try {
      logger(Status.Api, "QUERY challengeLeaderboardGet")

      const input = getInput(team!.id)

      const { data } = await dashboardChallengeLeaderboardGetQuery({
        variables: {
          input: { ...input, limit: 50, nextToken: leaderboardNextToken },
        },
      })

      // parse data
      let dataToSet = deepCopy(data.dashboardChallengeLeaderboardGet.items)
      dataToSet = dataToSet.filter(
        (item: any) =>
          item.user.first_name !== "deleted" &&
          item.user.last_name !== "deleted"
      )
      dataToSet.forEach((item: any) => {
        if (item.userPointsDeltaPercentage) {
          item.userPointsDeltaPercentage = roundWithDecimalPlaces(
            item.userPointsDeltaPercentage * 100,
            1
          )
        }
      })

      logger(Status.Info, "challenge leaderboard", [
        ...leaderboard,
        ...dataToSet,
      ])

      setLeaderboard([...leaderboard, ...dataToSet])
      setLeaderboardNextToken(data.dashboardChallengeLeaderboardGet.nextToken)

      return true
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} Leaderboard`)

      return false
    }
  }

  // load more groups leaderboard
  const loadMoreGroupsLeaderboardItems = async () => {
    try {
      logger(Status.Api, "QUERY challengeGroupLeaderboardGet")

      const input = getInput(team!.id)

      const { data } = await dashboardChallengeGroupLeaderboardGetQuery({
        variables: {
          input: { ...input, limit: 50, nextToken: groupsLeaderboardNextToken },
        },
      })

      logger(Status.Info, "challenge groups leaderboard", [
        ...leaderboard,
        ...data.dashboardChallengeGroupLeaderboardGet.items,
      ])

      setGroupsLeaderboard((current) => [
        ...current,
        ...data.dashboardChallengeGroupLeaderboardGet.items,
      ])
      setGroupsLeaderboardNextToken(
        data.dashboardChallengeGroupLeaderboardGet.nextToken
      )

      return true
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} Groups Leaderboard`)

      return false
    }
  }

  // load more education hours list
  const loadMoreEducationHoursUsersListItems = async () => {
    try {
      logger(Status.Api, "QUERY totalEducationHoursUsersGet")

      const input = getInput(team!.id)

      const { data } = await dashboardTotalEducationHoursUsersGetQuery({
        variables: {
          input: {
            ...input,
            limit: 50,
            nextToken: educationHoursUsersListNextToken,
          },
        },
      })

      logger(Status.Info, "education hours users list", [
        ...educationHoursUsersList,
        ...data.dashboardTotalEducationHoursUsersGet.items,
      ])

      setEducationHoursUsersList((current) => [
        ...current,
        ...data.dashboardTotalEducationHoursUsersGet.items,
      ])
      setEducationHoursUsersListNextToken(
        data.dashboardTotalEducationHoursUsersGet.nextToken
      )

      return true
    } catch (e) {
      console.log(e)

      setError(true)
      setErrorMessage(`${t("error")} Education Hours Users List`)

      return false
    }
  }

  // get all data
  const getOverviewData = async () => {
    setOverviewDataLoading(true)

    await Promise.all([
      getDashboardCardData(),
      getChallengeCardData(),
      getEducationHoursUsersList(),
      getDailyChallengeOverview(),
      getOverviewCardData(),
      getByCategoryCardData(),
      getTeamSavingsCardData(),
      getTotalNews(),
      getTotalSurveys(),
    ])

    setOverviewDataLoading(false)
  }

  return (
    <OverviewContext.Provider
      value={{
        overviewDataLoading,
        dashboardCardData,
        challenge,
        leaderboard,
        leaderboardNextToken,
        groupsLeaderboard,
        groupsLeaderboardNextToken,
        educationHoursUsersList,
        educationHoursUsersListNextToken,
        challengeOverviewData,
        overviewCardData,
        byCategoriesCardData,
        teamSavingsCardData,
        loadMoreLeaderboardItems,
        loadMoreGroupsLeaderboardItems,
        loadMoreEducationHoursUsersListItems,
        totalNews,
        totalSurveys,
        getOverviewData,
      }}
    >
      {children}
    </OverviewContext.Provider>
  )
}
export { OverviewController, OverviewContext }
